import React from 'react';
import useReportSpec from '../../reports/hooks/use-report-spec';
import ReportContextProvider, {
  useReportContext,
} from '../../reports/context/ReportContext';
import ResourceModals from '../../../views/MyDisk/components/ResourceModals';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { FiltersContextProvider } from '../../../components/v2/Filters/FiltersContext';
import { applyFilters, Filters } from '../../../core';
import { useUserSettings } from '../../settings/hooks/use-user-settings';
import Loader from '../../../components/v2/Loader';
import shallowEqual from '../../../utils/shallowEqual';
import Section from '../../../components/v2/Section/Section';

const SkeletonLoader = () => (
  <Box sx={{ p: 2, flexGrow: 1 }}>
    <Skeleton height="35px" width="30%" />
    <Box mb={1} />
    <Skeleton height="15px" width="40%" />
    <Box mb={1} />
    <Skeleton height="15px" width="10%" />
    <Skeleton height="300px" />
  </Box>
);

export const ReportConfigProvider = ({
  id,
  children,
}: React.PropsWithChildren<{ id: string }>) => {
  const { data, isLoading } = useReportSpec({ id });

  if (!data || isLoading) {
    return <SkeletonLoader />;
  }

  return <ReportContextProvider {...data}>{children}</ReportContextProvider>;
};

export const ReportFiltersProvider = ({
  children,
}: React.PropsWithChildren) => {
  const report = useReportContext();
  const { defaultValues } = report;
  const defaultEnabled = applyFilters(
    Filters.reports.filters.defaultEnabled,
    [],
    { report },
  );

  const savedFilters = useUserSettings(
    `report_list_filters_${report.id}`,
    defaultEnabled,
  );

  const savedValues = useUserSettings(
    `report_list_filters_values_${report.id}`,
    defaultValues,
  );

  if (savedFilters.isLoading || savedValues.isLoading) {
    return (
      <Section>
        <Loader />
      </Section>
    );
  }

  return (
    <FiltersContextProvider
      defaultValues={savedValues.value}
      defaultEnabled={['query', ...(savedFilters.value || [])]}
      onChange={(values) => {
        const nextValue = shallowEqual({ query: '' }, values) ? null : values;
        void savedValues.update(nextValue);
      }}
      onEnabledChange={(keys) => {
        void savedFilters.update(keys);
      }}
    >
      {children}
    </FiltersContextProvider>
  );
};

export default ({ children, id }: React.PropsWithChildren<{ id: string }>) => (
  <ReportConfigProvider id={id}>
    <ReportFiltersProvider>{children}</ReportFiltersProvider>
    <ResourceModals />
  </ReportConfigProvider>
);
